import { merge } from 'lodash-es';
import React, { createContext, useContext } from 'react';

import type { GutterProp } from '../../utilities/shared/Gutter';
import type { SpacingScale } from '../../utilities/shared/sizes';
import type { AccessLevel, AccessLevelProp } from '../../utilities/useAccessLevelContext';
import type { InternalProp } from '../../utilities/useInternal';
import { sizing } from '../../common/sizing';
import { LabelTooltip } from '../../components/Tooltip/Tooltip';
import { CopyBox } from '../../formatting/CopyBox/CopyBox';
import FeatureBadge from '../../formatting/FeatureBadge/FeatureBadge';
import { colors, darkThemeSelector, styled } from '../../stitches.config';
import { Body, BodySansSizes } from '../../text/Body';
import { Small, SmallSansSizes } from '../../text/Small';
import { Text } from '../../text/Text';
import { AlignStack } from '../../utilities/AlignStack/AlignStack';
import { spacingCSS } from '../../utilities/shared/Spacing';
import { toLowerCaseFirstCharacterSmartIsh } from '../../utilities/strings';
import {
  isAccessLevelVisible,
  UserAccessLevelContextProvider,
} from '../../utilities/useAccessLevelContext';
import { useAccessLevelOrInternal } from '../../utilities/useInternal';

export type DeprecatedSummaryListPropSize = 'x-small' | 'medium';
type DeprecatedSummaryListSize = DeprecatedSummaryListPropSize;
const DeprecatedSummaryListSizeContext = createContext<DeprecatedSummaryListSize | undefined>(
  undefined,
);
export const DeprecatedSummaryListSizeProvider = DeprecatedSummaryListSizeContext.Provider;
export const useDeprecatedSummaryListSize = (
  controlledValue?: DeprecatedSummaryListSize,
  defaultValue: DeprecatedSummaryListSize = 'medium',
) => {
  const deprecatedSummaryListSize = useContext(DeprecatedSummaryListSizeContext);
  return controlledValue ?? deprecatedSummaryListSize ?? defaultValue;
};

export type DeprecatedSummaryListPairsProps = Omit<
  DeprecatedSummaryListValueProps,
  'children' | 'internal'
> &
  Omit<DeprecatedSummaryListKeyProps, 'children'> & {
    /**
     * The label for the key.
     */
    label: React.ReactNode;
    /**
     * The value for the key.
     */
    value: React.ReactNode;
    /**
     * Access level for the pair.
     */
    accessLevel?: AccessLevelProp | null;
    /**
     * Boolean to show internal badge.
     */
    internal?: InternalProp;
  };

const DeprecatedSummaryListKeyLabel = styled(Small, {
  display: 'flex',
  minWidth: 'fit-content',
  maxWidth: '100%',
  color: '$$keyColor',
  fontWeight: '$bold',
});
DeprecatedSummaryListKeyLabel.displayName = 'DeprecatedSummaryListKeyLabel';

const DeprecatedSummaryListKeyInternal = styled('div', {
  display: 'flex',

  '@desktop': {
    marginTop: '-$2',
    marginBottom: '-$2',
  },
});
DeprecatedSummaryListKeyInternal.displayName = 'DeprecatedSummaryListKeyInternal';

const DeprecatedSummaryListKeyContainer = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  gap: '$4',
  width: '100%',
  padding: '$2 0',
});
DeprecatedSummaryListKeyContainer.displayName = 'DeprecatedSummaryListKeyContainer';

export type DeprecatedSummaryListKeyProps = {
  /**
   * Pass in any content as `children`.
   */
  children?: React.ReactNode;
  /**
   * Access level for the summary list.
   */
  accessLevel?: AccessLevel | null;
  /**
   * Boolean to show internal badge.
   */
  internal?: InternalProp;
  /**
   * Provide a tooltip for the key.
   */
  tooltip?: React.ReactNode;
};

export function DeprecatedSummaryListKey({
  children,
  accessLevel: accessLevelProp,
  internal,
  tooltip,
  ...remaining
}: DeprecatedSummaryListKeyProps) {
  const accessLevel = useAccessLevelOrInternal({ accessLevel: accessLevelProp, internal });
  const label = <DeprecatedSummaryListKeyLabel>{children}</DeprecatedSummaryListKeyLabel>;
  return (
    <DeprecatedSummaryListKeyContainer {...remaining}>
      <AlignStack
        gap={4}
        preset="small"
        width="fit-content"
        end={
          accessLevel &&
          isAccessLevelVisible(accessLevel) && (
            <DeprecatedSummaryListKeyInternal>
              <FeatureBadge arrangement="icon-only" type={accessLevel} size="x-small" />
            </DeprecatedSummaryListKeyInternal>
          )
        }
      >
        {tooltip ? (
          <LabelTooltip contents={tooltip} preset="small" truncate={false}>
            {label}
          </LabelTooltip>
        ) : (
          label
        )}
      </AlignStack>
    </DeprecatedSummaryListKeyContainer>
  );
}

const DeprecatedSummaryListValueContainer = styled(Text, {
  display: 'flex',
  width: '100%',
  maxWidth: '$700',
  wordBreak: 'break-word',
  color: '$$valueColor',

  variants: {
    size: {
      'x-small': {
        ...SmallSansSizes,
      },
      medium: {
        ...BodySansSizes,
      },
    },
  },
});
DeprecatedSummaryListValueContainer.displayName = 'DeprecatedSummaryListValueContainer';

type DeprecatedSummaryListValueProps = {
  /**
   * Pass in any content as `children`.
   */
  children?: React.ReactNode;
  /**
   * Boolean to allow the value to be copied to clipboard.
   */
  copyable?:
    | boolean
    | {
        label: React.ReactNode;
        value?: string;
      };
  /**
   * Set the size of the summary list.
   */
  size?: DeprecatedSummaryListPropSize;
};

export function DeprecatedSummaryListValue({
  children,
  copyable,
  size,
  ...remaining
}: DeprecatedSummaryListValueProps) {
  const deprecatedSummaryListSize = useDeprecatedSummaryListSize(size, 'medium');
  if (copyable && typeof copyable === 'object') {
    return (
      <DeprecatedSummaryListValueContainer size={deprecatedSummaryListSize} {...remaining}>
        <CopyBox aria-label={copyable.label} value={copyable.value || children}>
          {children}
        </CopyBox>
      </DeprecatedSummaryListValueContainer>
    );
  }

  if (children != null && children !== '') {
    return (
      <DeprecatedSummaryListValueContainer size={deprecatedSummaryListSize} {...remaining}>
        {children}
      </DeprecatedSummaryListValueContainer>
    );
  }

  return (
    <DeprecatedSummaryListValueContainer size={deprecatedSummaryListSize} {...remaining}>
      &mdash;
    </DeprecatedSummaryListValueContainer>
  );
}

export const DeprecatedSummaryListRowContainer = styled('div', {
  display: 'flex',
  width: '100%',
  $$keyColor: colors.bodyNeutralLight,
  $$valueColor: colors.headingNeutralLight,

  [darkThemeSelector]: {
    $$keyColor: colors.bodyNeutralDark,
    $$valueColor: colors.headingNeutralDark,
  },

  variants: {
    direction: {
      column: {
        flexDirection: 'column',
      },
      row: {
        flexDirection: 'row',
      },
    },
    variant: {
      negative: {
        $$keyColor: colors.bodyNegativeLight,
        $$valueColor: colors.headingNegativeLight,

        [darkThemeSelector]: {
          $$keyColor: colors.bodyNegativeDark,
          $$valueColor: colors.headingNegativeDark,
        },
      },
      attention: {
        $$keyColor: colors.bodyAttentionLight,
        $$valueColor: colors.headingAttentionLight,

        [darkThemeSelector]: {
          $$keyColor: colors.bodyAttentionDark,
          $$valueColor: colors.headingAttentionDark,
        },
      },
    },
  },
});
DeprecatedSummaryListRowContainer.displayName = 'DeprecatedSummaryListRowContainer';

export type DeprecatedSummaryListRowProps = {
  /**
   * Access level for the summary list row.
   */
  accessLevel?: AccessLevelProp | null;
  /**
   * Pass in any content as `children`.
   */
  children?: React.ReactNode;
  /**
   * Set the direction of the summary list row.
   */
  direction?: 'column' | 'row';
  /**
   * Boolean to show internal badge.
   */
  internal?: InternalProp;
  /**
   * Set if the row has a special variant like `attention` or `negative` to highlight an issue.
   */
  variant?: 'attention' | 'negative';
};

export function DeprecatedSummaryListRow({
  accessLevel: accessLevelProp,
  children,
  direction = 'column',
  internal,
  variant,
  ...remaining
}: DeprecatedSummaryListRowProps) {
  const accessLevel = useAccessLevelOrInternal({ accessLevel: accessLevelProp, internal });
  return (
    <UserAccessLevelContextProvider value={accessLevel}>
      <DeprecatedSummaryListRowContainer direction={direction} variant={variant} {...remaining}>
        {children}
      </DeprecatedSummaryListRowContainer>
    </UserAccessLevelContextProvider>
  );
}

const getCopyable = (pair: DeprecatedSummaryListPairsProps) =>
  typeof pair.copyable === 'object'
    ? {
        label:
          pair.copyable?.label ||
          `Copy ${pair.label && toLowerCaseFirstCharacterSmartIsh(pair.label?.toString())}`,
        value: pair.copyable?.value || pair.value?.toString(),
      }
    : {
        label: `Copy ${pair.label && toLowerCaseFirstCharacterSmartIsh(pair.label?.toString())}`,
        value: pair.value?.toString(),
      };

function DeprecatedSummaryListPairs({
  accessLevel: accessLevelProp,
  direction = 'column',
  internal,
  pairs,
  size,
}: {
  accessLevel?: AccessLevelProp;
  direction?: 'column' | 'row';
  internal?: InternalProp;
  pairs: DeprecatedSummaryListPairsProps[];
  size: DeprecatedSummaryListPropSize;
}) {
  const accessLevel = useAccessLevelOrInternal({ accessLevel: accessLevelProp, internal });

  if (pairs) {
    return pairs
      .filter((pair) => !!pair.label && !!pair.value)
      .map((pair, index) => {
        const key = index;
        return (
          <DeprecatedSummaryListRow
            key={`deprecatedSummaryList-${key}`}
            direction={direction}
            {...pair}
            accessLevel={accessLevel ?? pair.accessLevel}
          >
            {pair.label && <DeprecatedSummaryListKey>{pair.label}</DeprecatedSummaryListKey>}
            {pair.value && (
              <DeprecatedSummaryListValue size={size} copyable={pair.copyable && getCopyable(pair)}>
                {pair.value}
              </DeprecatedSummaryListValue>
            )}
          </DeprecatedSummaryListRow>
        );
      });
  }

  return null;
}

const DeprecatedSummaryListGroupLabel = styled(Body, {
  display: 'flex',
  alignItems: 'center',
  gap: '$6',
  fontWeight: '$bold',

  '&::after': {
    content: '',
    flex: 1,
    height: '$1',
    backgroundColor: colors.strokeNeutralLight,

    [darkThemeSelector]: {
      backgroundColor: colors.strokeNeutralDark,
    },
  },
});
DeprecatedSummaryListGroupLabel.displayName = 'DeprecatedSummaryListGroupLabel';

const DeprecatedSummaryListGroupItems = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '$12',
  width: '100%',
});
DeprecatedSummaryListGroupItems.displayName = 'DeprecatedSummaryListGroupItems';

const DeprecatedSummaryListGroupContainer = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '$8',
  width: '100%',
});
DeprecatedSummaryListGroupContainer.displayName = 'DeprecatedSummaryListGroupContainer';

export type DeprecatedSummaryListGroupProps = {
  /**
   * Pass in any content as `children`.
   */
  children?: React.ReactNode;
  /**
   * Boolean to show internal badge.
   */
  internal?: boolean;
  /**
   * Provide a label for the group with `label`.
   */
  label?: React.ReactNode;
  /**
   * Can pass in label-value pairs for the rows.
   */
  pairs?: DeprecatedSummaryListPairsProps[];
  /**
   * Set the size of the summary list.
   */
  size?: DeprecatedSummaryListPropSize;
};

export function DeprecatedSummaryListGroup({
  children,
  internal,
  label,
  pairs,
  size,
  ...remaining
}: DeprecatedSummaryListGroupProps) {
  const deprecatedSummaryListSize = useDeprecatedSummaryListSize(size, 'medium');
  return (
    <DeprecatedSummaryListGroupContainer {...remaining}>
      <DeprecatedSummaryListGroupLabel>{label}</DeprecatedSummaryListGroupLabel>
      <DeprecatedSummaryListGroupItems>
        {children}
        {pairs && (
          <DeprecatedSummaryListPairs
            internal={internal}
            pairs={pairs}
            size={deprecatedSummaryListSize}
          />
        )}
      </DeprecatedSummaryListGroupItems>
    </DeprecatedSummaryListGroupContainer>
  );
}

export const DeprecatedSummaryListContainer = styled('div', {
  width: '100%',

  variants: {
    direction: {
      column: {
        display: 'flex',
        flexDirection: 'column',
      },
      row: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax($224, max-content))',
        gridTemplateRows: 'repeat(auto-fit, minmax(min-content, 0))',
        gridAutoFlow: 'row dense',

        [`& ${DeprecatedSummaryListRowContainer}`]: {
          width: 'auto',
        },

        [`& ${DeprecatedSummaryListKeyLabel}`]: {
          whiteSpace: 'nowrap',
        },
      },
    },
    gutter: {
      all: {
        padding: sizing.contentSquish,
      },
      vertical: {
        padding: sizing.contentEndsOnly,
      },
      horizontal: {
        padding: sizing.contentSidesOnly,
      },
      top: {
        paddingTop: sizing.contentEnds,
      },
      end: {
        paddingRight: sizing.contentSides,
      },
      bottom: {
        paddingBottom: sizing.contentEnds,
      },
      start: {
        paddingLeft: sizing.contentSides,
      },
      none: {},
    },
    size: {
      'x-small': {},
      medium: {},
    },
  },
});
DeprecatedSummaryListContainer.displayName = 'DeprecatedSummaryListContainer';

export type DeprecatedSummaryListProps = {
  /**
   * Pass in any content as `children`.
   */
  children?: React.ReactNode;
  /**
   * Set the direction of the summary list items.
   */
  direction?: 'column' | 'row';
  /**
   * Set the gap between the children.
   */
  gap?: SpacingScale;
  /**
   * Access level of the summary list.
   */
  accessLevel?: AccessLevelProp | null;
  /**
   * Boolean to show internal badge.
   */
  internal?: InternalProp;
  /**
   * Can pass in label-value pairs for the rows.
   */
  pairs?: DeprecatedSummaryListPairsProps[];
  /**
   * Set the size of the summary list.
   */
  size?: DeprecatedSummaryListPropSize;
} & GutterProp;

export function DeprecatedSummaryList({
  children,
  direction = 'column',
  accessLevel: accessLevelProp,
  internal,
  gap = 20,
  gutter = { edge: 'all' },
  pairs,
  size = 'medium',
  ...remaining
}: DeprecatedSummaryListProps) {
  const accessLevel = useAccessLevelOrInternal({ accessLevel: accessLevelProp, internal });
  const css = merge(spacingCSS(gap));
  return (
    <DeprecatedSummaryListSizeProvider value={size}>
      <UserAccessLevelContextProvider value={accessLevel}>
        <DeprecatedSummaryListContainer
          direction={direction}
          gutter={gutter.edge}
          size={size}
          css={css}
          {...remaining}
        >
          {children}
          {pairs && <DeprecatedSummaryListPairs internal={internal} pairs={pairs} size={size} />}
        </DeprecatedSummaryListContainer>
      </UserAccessLevelContextProvider>
    </DeprecatedSummaryListSizeProvider>
  );
}
